import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Spinner, showToast } from '../components/UI';

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

/* ── Load GrapesJS from your own /public folder (self-hosted) ── */
function useGrapesJS() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (window.grapesjs) { setReady(true); return; }

    // Load CSS
    if (!document.getElementById('grapesjs-css')) {
      const link = document.createElement('link');
      link.id   = 'grapesjs-css';
      link.rel  = 'stylesheet';
      // Self-hosted: copy grapes.min.css from node_modules/grapesjs/dist/css/ → public/vendors/grapesjs/
      link.href = '/vendors/grapesjs/grapes.min.css';
      document.head.appendChild(link);
    }

    // Load JS
    const script    = document.createElement('script');
    script.src      = '/vendors/grapesjs/grapes.min.js';
    script.onload   = () => setReady(true);
    script.onerror  = () => setError('Failed to load GrapesJS. Make sure grapes.min.js is in /public/vendors/grapesjs/');
    document.head.appendChild(script);
  }, []);

  return { ready, error };
}

/* ═══════════════════════════════════════════════════════════ */
export default function ArtifactEditor() {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const isMobile    = useIsMobile();
  const editorRef   = useRef(null);
  const containerRef = useRef(null);

  const [artifact,    setArtifact]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [fetchError,  setFetchError]  = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewHtml,  setPreviewHtml]  = useState('');

  const { ready: gjsReady, error: gjsError } = useGrapesJS();

  /* ── 1. Fetch artifact ── */
  useEffect(() => {
    setLoading(true);
    api.getArtifact(id)
      .then(setArtifact)
      .catch(e => setFetchError(e.message || 'Failed to load artifact'))
      .finally(() => setLoading(false));
  }, [id]);

  /* ── 2. Init GrapesJS once both artifact + library are ready ── */
  useEffect(() => {
    if (!gjsReady || !artifact?.body || !containerRef.current) return;
    if (editorRef.current) return; // already initialised

    const editor = window.grapesjs.init({
      container: containerRef.current,
      height:    '100%',
      width:     '100%',

      // Load the artifact HTML
      components: artifact.body,
      style:      '',

      storageManager: false, // we handle save ourselves

      // Panels – we build our own top-bar, so hide the default one
      panels: { defaults: [] },

      // Keep essential blocks + style manager
      blockManager: {
        appendTo: '#gjs-blocks',
        blocks: [],
      },

      styleManager: {
        appendTo: '#gjs-styles',
        sectors: [
          {
            name: 'Typography',
            open: true,
            properties: [
              { property: 'font-family' },
              { property: 'font-size',   type: 'integer', units: ['px','em','rem','%'] },
              { property: 'font-weight' },
              { property: 'color',       type: 'color' },
              { property: 'text-align',  type: 'radio',
                options: [
                  { value: 'left',    className: 'fa fa-align-left' },
                  { value: 'center',  className: 'fa fa-align-center' },
                  { value: 'right',   className: 'fa fa-align-right' },
                ],
              },
              { property: 'line-height', type: 'integer', units: ['px','em',''] },
            ],
          },
          {
            name: 'Background',
            open: false,
            properties: [
              { property: 'background-color', type: 'color' },
            ],
          },
          {
            name: 'Spacing',
            open: false,
            properties: [
              { property: 'padding',  type: 'composite',
                properties: [
                  { property: 'padding-top',    type: 'integer', units: ['px','em','%'] },
                  { property: 'padding-right',  type: 'integer', units: ['px','em','%'] },
                  { property: 'padding-bottom', type: 'integer', units: ['px','em','%'] },
                  { property: 'padding-left',   type: 'integer', units: ['px','em','%'] },
                ],
              },
              { property: 'margin', type: 'composite',
                properties: [
                  { property: 'margin-top',    type: 'integer', units: ['px','em','%'] },
                  { property: 'margin-right',  type: 'integer', units: ['px','em','%'] },
                  { property: 'margin-bottom', type: 'integer', units: ['px','em','%'] },
                  { property: 'margin-left',   type: 'integer', units: ['px','em','%'] },
                ],
              },
            ],
          },
          {
            name: 'Border',
            open: false,
            properties: [
              { property: 'border-radius', type: 'integer', units: ['px','%'] },
              { property: 'border-width',  type: 'integer', units: ['px'] },
              { property: 'border-style',  type: 'select',
                options: [
                  { value: 'none' }, { value: 'solid' }, { value: 'dashed' }, { value: 'dotted' },
                ],
              },
              { property: 'border-color', type: 'color' },
            ],
          },
        ],
      },

      layerManager:  { appendTo: '#gjs-layers' },
      traitManager:  { appendTo: '#gjs-traits' },
      deviceManager: { devices: [
        { name: 'Desktop',  width: '' },
        { name: 'Tablet',   width: '768px' },
        { name: 'Mobile',   width: '375px' },
      ]},
    });

    editorRef.current = editor;

    // Cleanup on unmount
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [gjsReady, artifact]);

  /* ── Handlers ── */
  const handlePreview = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.getHtml();
    const css  = editorRef.current.getCss();
    setPreviewHtml(`<!DOCTYPE html><html><head><style>${css}</style></head><body>${html}</body></html>`);
    setIsPreviewing(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editorRef.current || !artifact) return;
    const html = editorRef.current.getHtml();
    const css  = editorRef.current.getCss();
    const body = `<!DOCTYPE html><html><head><style>${css}</style></head><body>${html}</body></html>`;

    setSaving(true);
    try {
      await api.createArtifactVersion(artifact.workspace_id, id, {
        body,
        html,
        css,
        title:      artifact.title,
        type:       artifact.type,
        agent_type: artifact.agent_type,
      });
      showToast('New version saved ✓');
      navigate(`/dashboard/artifacts/${id}`);
    } catch (e) {
      showToast(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [artifact, id, navigate]);

  const handleCancel = useCallback(() => {
    navigate(`/dashboard/artifacts/${id}`);
  }, [id, navigate]);

  const setDevice = useCallback((deviceName) => {
    if (!editorRef.current) return;
    editorRef.current.setDevice(deviceName);
  }, []);

  /* ── Render states ── */
  if (loading) return <div style={centeredStyle}><Spinner size={28} /></div>;

  if (fetchError || !artifact) {
    return (
      <div style={centeredStyle}>
        <p style={{ color: 'var(--ink-mute)', marginBottom: '16px' }}>{fetchError || 'Artifact not found'}</p>
        <button onClick={handleCancel} style={ghostBtnStyle}>← Back</button>
      </div>
    );
  }

  if (gjsError) {
    return (
      <div style={centeredStyle}>
        <p style={{ color: 'var(--red, #c0392b)', marginBottom: '8px' }}>⚠️ Editor failed to load</p>
        <p style={{ color: 'var(--ink-mute)', fontSize: '13px', maxWidth: '420px', textAlign: 'center', marginBottom: '16px' }}>{gjsError}</p>
        <button onClick={handleCancel} style={ghostBtnStyle}>← Back to artifact</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--paper)', overflow: 'hidden' }}>

      {/* ── Top Bar ── */}
      <header style={{
        height:        '52px',
        background:    'rgba(245,242,235,0.97)',
        backdropFilter:'blur(10px)',
        borderBottom:  '1px solid var(--paper-3)',
        display:       'flex',
        alignItems:    'center',
        justifyContent:'space-between',
        padding:       '0 16px',
        gap:           '12px',
        flexShrink:    0,
        zIndex:        100,
      }}>
        {/* Left: back + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
          <button onClick={handleCancel} style={ghostBtnStyle} title="Cancel and go back">
            ← Cancel
          </button>
          <div style={{ width: '1px', height: '20px', background: 'var(--paper-3)', flexShrink: 0 }} />
          <span style={{
            fontSize: '13px', fontWeight: '500',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: 'var(--ink-soft)',
          }}>
            Editing: <span style={{ color: 'var(--ink)' }}>{artifact.title}</span>
          </span>
          <span style={{
            fontSize: '11px', fontWeight: '600', padding: '2px 8px',
            borderRadius: '100px', background: 'rgba(59,130,246,0.1)', color: '#378add',
            flexShrink: 0,
          }}>
            New version
          </span>
        </div>

        {/* Center: device switcher (desktop only) */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'var(--paper-2, #ede9e0)', borderRadius: '8px', padding: '3px' }}>
            {[
              { label: '🖥', name: 'Desktop', title: 'Desktop' },
              { label: '📱', name: 'Tablet',  title: 'Tablet' },
              { label: '📲', name: 'Mobile',  title: 'Mobile' },
            ].map(d => (
              <button
                key={d.name}
                onClick={() => setDevice(d.name)}
                title={d.title}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '4px 10px', borderRadius: '6px', fontSize: '14px',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                {d.label}
              </button>
            ))}
          </div>
        )}

        {/* Right: actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <button
            onClick={handlePreview}
            style={{
              ...actionBtnStyle,
              background: 'var(--white)',
              border: '1px solid var(--paper-3)',
              color: 'var(--ink)',
            }}
          >
            👁 {!isMobile && 'Preview'}
          </button>
          <button
            onClick={handleCancel}
            style={{
              ...actionBtnStyle,
              background: 'var(--white)',
              border: '1px solid var(--paper-3)',
              color: 'var(--ink-soft)',
            }}
          >
            ✕ {!isMobile && 'Discard'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              ...actionBtnStyle,
              background: saving ? 'var(--ink-mute)' : 'var(--ink)',
              color: 'var(--paper)',
              border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              minWidth: '90px',
            }}
          >
            {saving ? <Spinner size={14} color="white" /> : `💾 ${!isMobile ? 'Save version' : 'Save'}`}
          </button>
        </div>
      </header>

      {/* ── Editor Body ── */}
      {!gjsReady ? (
        <div style={centeredStyle}>
          <Spinner size={28} />
          <span style={{ color: 'var(--ink-mute)', fontSize: '13px', marginTop: '12px' }}>Loading editor…</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

          {/* Left sidebar: Layers */}
          {!isMobile && (
            <aside style={sidebarStyle}>
              <SidebarSection label="Layers">
                <div id="gjs-layers" style={{ flex: 1, overflowY: 'auto' }} />
              </SidebarSection>
            </aside>
          )}

          {/* Canvas */}
          <div
            ref={containerRef}
            style={{ flex: 1, minWidth: 0, height: '100%', overflow: 'hidden' }}
          />

          {/* Right sidebar: Styles + Traits */}
          {!isMobile && (
            <aside style={{ ...sidebarStyle, borderLeft: '1px solid var(--paper-3)', borderRight: 'none' }}>
              <SidebarSection label="Style">
                <div id="gjs-styles" style={{ flex: 1, overflowY: 'auto' }} />
              </SidebarSection>
              <div style={{ height: '1px', background: 'var(--paper-3)', margin: '0 -16px' }} />
              <SidebarSection label="Properties">
                <div id="gjs-traits" style={{ flex: 1, overflowY: 'auto' }} />
              </SidebarSection>
            </aside>
          )}
        </div>
      )}

      {/* ── Preview Modal ── */}
      {isPreviewing && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Modal top bar */}
          <div style={{
            height: '52px', background: 'var(--paper)',
            borderBottom: '1px solid var(--paper-3)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 20px', flexShrink: 0,
          }}>
            <span style={{ fontWeight: '600', fontSize: '14px' }}>Preview</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ ...actionBtnStyle, background: 'var(--ink)', color: 'var(--paper)', border: 'none' }}
              >
                {saving ? <Spinner size={14} color="white" /> : '💾 Save version'}
              </button>
              <button
                onClick={() => setIsPreviewing(false)}
                style={{ ...actionBtnStyle, background: 'var(--white)', border: '1px solid var(--paper-3)', color: 'var(--ink)' }}
              >
                ✕ Close
              </button>
            </div>
          </div>
          {/* Preview iframe */}
          <iframe
            srcDoc={previewHtml}
            title="Preview"
            sandbox="allow-scripts allow-same-origin"
            style={{ flex: 1, border: 'none', width: '100%', background: 'white' }}
          />
        </div>
      )}
    </div>
  );
}

/* ── Sub-components ── */
function SidebarSection({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '12px 16px 0', minHeight: 0, flex: 1 }}>
      <div style={{
        fontSize: '10px', fontWeight: '600', letterSpacing: '0.1em',
        textTransform: 'uppercase', color: 'var(--ink-mute)',
        marginBottom: '10px', paddingBottom: '6px',
        borderBottom: '1px solid var(--paper-3)',
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

/* ── Styles ── */
const centeredStyle = {
  display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
  height: '100vh', gap: '12px',
};

const ghostBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: '13px', color: 'var(--ink-soft)', padding: '4px 0',
};

const actionBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '7px 14px', borderRadius: 'var(--r, 8px)',
  fontSize: '13px', fontWeight: '500', cursor: 'pointer',
  transition: 'opacity 0.15s',
};

const sidebarStyle = {
  width: '220px',
  flexShrink: 0,
  borderRight: '1px solid var(--paper-3)',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  background: 'rgba(255,255,255,0.4)',
};
